import React, { useEffect, useMemo, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'

import {
  confirmLandingPageDonation,
  createLandingPageDonationIntent,
} from '../../utilities/api'
import { formatCurrency } from '../../utilities/helpers'
import FundraiserShareModal from './FundraiserShareModal'

const parseAmount = (value) => {
  const amount = Number(String(value || '').replace(/[^0-9.]/g, ''))
  return Number.isFinite(amount) && amount > 0 ? amount : 0
}

const getStripeAppearance = (accentColor) => ({
  theme: 'flat',
  variables: {
    fontFamily: '"Poppins", sans-serif',
    fontSizeBase: '14px',
    borderRadius: '8px',
    colorPrimary: accentColor,
    colorBackground: '#FFFFFF',
    colorText: '#212529',
    colorTextSecondary: '#6c757d',
    spacingUnit: '10px',
    spacingGridRow: '16px',
  },
  rules: {
    '.Input': {
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.12)',
      padding: '12px 14px',
    },
    '.Input:focus': {
      outline: '0',
      boxShadow: `0px 0px 0px 2px ${accentColor}`,
    },
    '.Tab': {
      backgroundColor: '#FFFFFF',
      boxShadow: '0px 0px 0px 1px rgba(0, 0, 0, 0.12)',
      border: 'none',
    },
    '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': {
      border: 'none',
      boxShadow: `0px 0px 0px 2px ${accentColor}`,
    },
    '.Label': {
      fontWeight: '600',
      color: '#495057',
      marginBottom: '6px',
    },
  },
})

function DonationChargeSummary({
  donationAmount,
  platformFee,
  processingFee,
  totalCharge,
}) {
  return (
    <div className="fundraiser-donation-summary">
      <h3 className="fundraiser-donation-summary__title">Your donation</h3>
      <div className="fundraiser-donation-summary__rows">
        <div className="fundraiser-donation-summary__row">
          <span>Your donation</span>
          <span>{formatCurrency(donationAmount)}</span>
        </div>
        {platformFee > 0 && (
          <div className="fundraiser-donation-summary__row">
            <span>Platform fee</span>
            <span>{formatCurrency(platformFee)}</span>
          </div>
        )}
        {processingFee > 0 && (
          <div className="fundraiser-donation-summary__row">
            <span>Processing fee</span>
            <span>{formatCurrency(processingFee)}</span>
          </div>
        )}
      </div>
      <div className="fundraiser-donation-summary__total">
        <span>Total due today</span>
        <span>{formatCurrency(totalCharge)}</span>
      </div>
    </div>
  )
}

function DonationPaymentForm({
  accentColor,
  donationAmount,
  platformFee,
  processingFee,
  totalCharge,
  onSuccess,
  onError,
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!stripe || !elements) return

    setIsSubmitting(true)
    onError('')

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      })

      if (error) {
        onError(error.message || 'Payment failed. Please try again.')
        return
      }

      if (paymentIntent?.status === 'succeeded') {
        await confirmLandingPageDonation(paymentIntent.id)
        onSuccess()
      }
    } catch (submitError) {
      console.error('Landing page donation failed:', submitError)
      onError('Unable to complete your donation right now.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="fundraiser-payment-element">
        <PaymentElement
          options={{
            wallets: {
              link: 'never',
            },
          }}
        />
      </div>
      <DonationChargeSummary
        donationAmount={donationAmount}
        platformFee={platformFee}
        processingFee={processingFee}
        totalCharge={totalCharge}
      />
      <Button
        type="submit"
        disabled={!stripe || isSubmitting}
        className="fundraiser-donate-btn"
        style={{ backgroundColor: accentColor, borderColor: accentColor }}
      >
        {isSubmitting ? 'Processing...' : 'Complete Donation'}
      </Button>
    </form>
  )
}

export default function FundraisingLandingDonation({
  campaign,
  organizationUUID,
  organizationSlug,
  initialParticipantUuid = '',
  embedded = false,
  sidebar = false,
}) {
  const [customAmount, setCustomAmount] = useState('')
  const [amountTouched, setAmountTouched] = useState(false)
  const [participantUuid, setParticipantUuid] = useState(initialParticipantUuid)
  const [donorName, setDonorName] = useState('')
  const [donorEmail, setDonorEmail] = useState('')
  const [anonymous, setAnonymous] = useState(false)
  const [donorMessage, setDonorMessage] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [stripePromise, setStripePromise] = useState(null)
  const [donationPricing, setDonationPricing] = useState({
    donationAmount: 0,
    platformFee: 0,
    processingFee: 0,
    totalCharge: 0,
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isLoadingIntent, setIsLoadingIntent] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined') return ''
    return window.location.href
  }, [])

  const accentColor = campaign?.primaryColor || '#0057b8'
  const presetAmounts = useMemo(
    () =>
      Array.isArray(campaign?.suggestedAmounts)
        ? campaign.suggestedAmounts
        : [],
    [campaign]
  )

  useEffect(() => {
    setParticipantUuid(initialParticipantUuid || '')
  }, [initialParticipantUuid])

  const effectiveAmount = parseAmount(customAmount)
  const suggestedAmountIndex = Math.floor(presetAmounts.length / 2)

  const startDonation = async () => {
    setAmountTouched(true)
    const amount = effectiveAmount
    if (amount <= 0) {
      setError('Select or enter a donation amount.')
      return
    }
    if (!anonymous && !donorEmail.trim()) {
      setError('Email is required unless donating anonymously.')
      return
    }

    setError('')
    setIsLoadingIntent(true)
    setClientSecret('')
    setStripePromise(null)

    try {
      const response = await createLandingPageDonationIntent(campaign.slug, {
        organizationUUID: organizationUUID || campaign.organizationUUID,
        organizationSlug: organizationSlug || undefined,
        donationAmount: amount,
        participantUuid,
        anonymous,
        donorMessage,
        donorName: anonymous ? '' : donorName,
        donorEmail: anonymous ? '' : donorEmail,
      })

      const data = response?.data || {}
      if (!data.clientSecret) {
        throw new Error('Missing payment session')
      }

      setDonationPricing({
        donationAmount: Number(data.donationAmount || amount),
        platformFee: Number(data.platformFee || 0),
        processingFee: Number(data.processingFee || 0),
        totalCharge: Number(data.totalCharge || amount),
      })
      setClientSecret(data.clientSecret)

      const stripeAccount = data.connectedAccountId || undefined
      const stripe = stripeAccount
        ? await loadStripe(process.env.REACT_APP_STRIPE_KEY, {
            stripeAccount,
          })
        : await loadStripe(process.env.REACT_APP_STRIPE_KEY)
      setStripePromise(stripe)
    } catch (intentError) {
      console.error('Failed to start landing page donation:', intentError)
      setError('Unable to start checkout. Please try again.')
    } finally {
      setIsLoadingIntent(false)
    }
  }

  if (!campaign?.enableLandingPageDonation) return null

  const formContent = (
    <>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {!clientSecret ? (
        <Stack gap={3}>
          {sidebar && (
            <>
              <Button
                type="button"
                className="fundraiser-donate-btn"
                style={{
                  backgroundColor: accentColor,
                  borderColor: accentColor,
                }}
                onClick={() => setShowShareModal(true)}
              >
                Share
              </Button>
              <FundraiserShareModal
                show={showShareModal}
                onHide={() => setShowShareModal(false)}
                campaignTitle={campaign?.title}
                shareUrl={shareUrl}
              />
            </>
          )}

          {campaign.participants?.length > 0 && (
            <Form.Group>
              <Form.Label>Support</Form.Label>
              <Form.Select
                value={participantUuid}
                onChange={(event) => setParticipantUuid(event.target.value)}
              >
                <option value="">General campaign fund</option>
                {campaign.participants.map((participant) => (
                  <option key={participant.uuid} value={participant.uuid}>
                    {participant.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <div className="fundraiser-amount-section">
            {!sidebar && (
              <div className="small fw-semibold mb-2">Choose an amount</div>
            )}
            {presetAmounts.length > 0 && (
              <div className="fundraiser-amount-presets">
                {presetAmounts.map((amount, index) => {
                  const parsed = parseAmount(amount)
                  const isSelected =
                    effectiveAmount > 0 && effectiveAmount === parsed
                  const isSuggested = index === suggestedAmountIndex
                  return (
                    <button
                      key={amount}
                      type="button"
                      className={`fundraiser-amount-preset${
                        isSelected ? ' fundraiser-amount-preset--selected' : ''
                      }`}
                      style={
                        isSelected
                          ? {
                              backgroundColor: accentColor,
                              borderColor: accentColor,
                              color: '#fff',
                            }
                          : { borderColor: accentColor, color: accentColor }
                      }
                      onClick={() => {
                        setCustomAmount(String(parsed))
                        setAmountTouched(false)
                        setError('')
                      }}
                    >
                      <span>{amount}</span>
                      {isSuggested && (
                        <span className="fundraiser-amount-preset__badge">
                          Suggested
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}

            {campaign.allowCustomAmount !== false && (
              <div
                className={`fundraiser-amount-input${
                  amountTouched && effectiveAmount <= 0
                    ? ' fundraiser-amount-input--invalid'
                    : ''
                }`}
                style={{ '--fundraiser-accent': accentColor }}
              >
                <div className="fundraiser-amount-input__currency">
                  <span className="fundraiser-amount-input__symbol">$</span>
                  <span className="fundraiser-amount-input__code">USD</span>
                </div>
                <div className="fundraiser-amount-input__value">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="fundraiser-amount-input__field"
                    placeholder="0"
                    value={customAmount}
                    aria-label="Donation amount"
                    style={{
                      width: `${Math.max(customAmount.length || 1, 1)}ch`,
                    }}
                    onChange={(event) => {
                      const nextValue = event.target.value.replace(
                        /[^0-9.]/g,
                        ''
                      )
                      setCustomAmount(nextValue)
                      setAmountTouched(false)
                      setError('')
                    }}
                    onBlur={() => setAmountTouched(true)}
                  />
                  <div className="fundraiser-amount-input__cents">.00</div>
                </div>
              </div>
            )}

            {amountTouched && effectiveAmount <= 0 && (
              <div className="fundraiser-amount-error">
                Please enter a donation amount
              </div>
            )}
          </div>

          {!anonymous && (
            <>
              <Form.Control
                placeholder="Your name"
                value={donorName}
                onChange={(event) => setDonorName(event.target.value)}
              />
              <Form.Control
                type="email"
                placeholder="Email"
                value={donorEmail}
                onChange={(event) => setDonorEmail(event.target.value)}
              />
            </>
          )}

          {campaign.allowAnonymousDonation && (
            <Form.Check
              type="checkbox"
              label="Donate anonymously"
              checked={anonymous}
              onChange={(event) => setAnonymous(event.target.checked)}
            />
          )}

          {campaign.allowDonorMessage && (
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Message (optional)"
              value={donorMessage}
              onChange={(event) => setDonorMessage(event.target.value)}
            />
          )}

          <Button
            onClick={startDonation}
            disabled={isLoadingIntent}
            className={sidebar ? 'fundraiser-donate-btn' : undefined}
            style={{ backgroundColor: accentColor, borderColor: accentColor }}
          >
            {isLoadingIntent
              ? 'Preparing checkout...'
              : sidebar
                ? campaign.ctaButton || 'Donate now'
                : 'Continue to payment'}
          </Button>
        </Stack>
      ) : (
        stripePromise && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: getStripeAppearance(accentColor),
            }}
          >
            <DonationPaymentForm
              accentColor={accentColor}
              donationAmount={donationPricing.donationAmount}
              platformFee={donationPricing.platformFee}
              processingFee={donationPricing.processingFee}
              totalCharge={donationPricing.totalCharge}
              onSuccess={() => setSuccess(true)}
              onError={setError}
            />
          </Elements>
        )
      )}
    </>
  )

  if (success) {
    return (
      <Alert variant="success" className="mb-0">
        Thank you for your donation!
      </Alert>
    )
  }

  if (embedded) {
    return formContent
  }

  return (
    <Card className="card-xl border-0 shadow-sm mb-4">
      <Card.Body className="p-4">
        <h2 className="h4 mb-3">Donate now</h2>
        {formContent}
      </Card.Body>
    </Card>
  )
}
